import { z } from "zod";
import { Id, IsoDateTime } from "./common.ts";

/** `Gift` lifecycle. Spec-locked to _spec-backbone.md §4. */
export const GiftStatus = z.enum([
  "pending",
  "accepted",
  "rejected",
  "expired",
  "staff_rejected",
  "fulfilled",
  "refunded",
  "voided",
]);
export type GiftStatus = z.infer<typeof GiftStatus>;

/** Who a gift is addressed to: a whole party/table, or one specific person. */
export const GiftTargetType = z.enum(["party", "person"]);
export type GiftTargetType = z.infer<typeof GiftTargetType>;

/**
 * A "Just One Drink" gift. Sent by a party to either another party (table) or a
 * specific person at a table. Contact exchange is a separate consent-gated flow
 * (ContactRequest), not part of the gift.
 */
export const Gift = z.object({
  id: Id,
  sessionId: Id,
  senderPartyId: Id,
  senderUserId: Id.optional(),
  targetType: GiftTargetType,
  recipientPartyId: Id,
  /** Required when `targetType` is `person`; absent for `party` gifts. */
  recipientCheckInId: Id.optional(),
  menuItemId: Id,
  /** Optional short message attached to the drink. Subject to moderation. */
  message: z.string().max(140).optional(),
  status: GiftStatus,
  createdAt: IsoDateTime,
  respondedAt: IsoDateTime.optional(),
  expiresAt: IsoDateTime.optional(),
});
export type Gift = z.infer<typeof Gift>;

export const SendGift = z.object({
  targetType: GiftTargetType,
  recipientPartyId: Id,
  /** Required when `targetType` is `person`. */
  recipientCheckInId: Id.optional(),
  menuItemId: Id,
  message: z.string().max(140).optional(),
});
export type SendGift = z.infer<typeof SendGift>;

export const RespondGift = z.object({
  accept: z.boolean(),
});
export type RespondGift = z.infer<typeof RespondGift>;
